import { useMemo, useState } from 'react';
import { getTimeStamp } from '../../../../../../utils/dateUtils';
import { isEmpty } from '../../../../../../utils/utils';
import { useLatestRequestGuard } from '../../../../../../common/hooks/useLatestRequestGuard';
import { fetchLegalCaseDetailData } from '../../../../../../store/dashboard/tables/tables_apiHelpers';
import type { Case } from '../legalCasesType';

// Polus sends entityId on the group (flattened here into groupEntityId -- see
// flattenAndLimitCases), so that is the primary source; a case-level entityId is a fallback.
// A row's displayed cino can be empty (polus blanks a case number it made up); caseKey never is.
const detailLookupKey = (singleCase: Case): string => singleCase.caseKey ?? singleCase.cino ?? '';

// The organisation is part of the key: the hook outlives a route change to another company, and one
// company's details must never be served for another's row.
const detailCacheKey = (assesseeOrgId: string, singleCase: Case): string =>
    `${assesseeOrgId}::${detailLookupKey(singleCase)}::${singleCase.groupEntityId ?? singleCase.entityId ?? ''}`;

// List rows carry a slim caseDetails (party names, category) with no match evidence; a full row
// always has either the evidence or the reason it is absent, so only a full row can skip the fetch.
const hasFullDetails = (singleCase: Case): boolean =>
    singleCase.caseDetails?.matchEvidence != null || singleCase.caseDetails?.matchEvidenceAbsentReason != null;

type DetailsStatus = 'idle' | 'loading' | 'error';

export const useCaseDetailsDrawer = (assesseeOrgId: string) => {
    // Remembered with the organisation it was opened under, so a selection made for one company is
    // simply not the selection once the route moves to another -- no effect needed to clear it.
    const [selection, setSelection] = useState<{ orgId: string; singleCase: Case } | null>(null);
    const selectedCase = selection?.orgId === assesseeOrgId ? selection.singleCase : null;
    // Keyed by organisation+case key+entityId so reopening an already-fetched case never refetches.
    const [fetchedDetailsByKey, setFetchedDetailsByKey] = useState<Record<string, Case>>({});
    // A single status instead of two independent booleans -- loading and error can never both be
    // true, so there's no render-order precedence to get right.
    const [detailsStatus, setDetailsStatus] = useState<DetailsStatus>('idle');
    // Bumped on every open/close so a slow, superseded fetch can never flip loading/error state
    // for whatever case is selected by the time it resolves.
    const detailsRequestGuard = useLatestRequestGuard();

    const openCaseDrawer = (singleCase: Case) => {
        const token = detailsRequestGuard.startRequest();
        setSelection({ orgId: assesseeOrgId, singleCase });

        const key = detailCacheKey(assesseeOrgId, singleCase);
        if (hasFullDetails(singleCase) || fetchedDetailsByKey[key]) {
            setDetailsStatus('idle');
            return;
        }

        setDetailsStatus('loading');
        fetchLegalCaseDetailData(
            {
                assesseeOrgId,
                requestId: getTimeStamp(),
                cino: detailLookupKey(singleCase),
                entityId: singleCase.groupEntityId ?? singleCase.entityId ?? '',
            },
            {
                onSuccess: (data: Case) => {
                    if (!detailsRequestGuard.isCurrent(token)) return;
                    // An empty payload is a failed load: cached, it would count as a hit above and
                    // pin the sparse row, so reopening could never retry.
                    if (isEmpty(data)) {
                        setDetailsStatus('error');
                        return;
                    }
                    setDetailsStatus('idle');
                    setFetchedDetailsByKey((prev) => ({ ...prev, [key]: data }));
                },
                onError: () => {
                    if (!detailsRequestGuard.isCurrent(token)) return;
                    setDetailsStatus('error');
                },
            },
        );
    };

    const closeCaseDrawer = () => {
        detailsRequestGuard.startRequest();
        setSelection(null);
    };

    // The row as fetched from fetchLegalCases, overlaid with its fetched detail once resolved --
    // renders correctly on every tick: pre-fetch (sparse row), post-fetch (merged), and for rows
    // that already carried caseDetails (cachedDetails stays undefined, so this is just selectedCase).
    const caseForDetails = useMemo(() => {
        if (!selectedCase) return null;
        return { ...selectedCase, ...fetchedDetailsByKey[detailCacheKey(assesseeOrgId, selectedCase)] };
    }, [assesseeOrgId, selectedCase, fetchedDetailsByKey]);

    return {
        selectedCase,
        caseForDetails,
        openCaseDrawer,
        closeCaseDrawer,
        // A fetch still running for the previous company's case is not this company's spinner or error.
        isFetchingCaseDetails: selectedCase !== null && detailsStatus === 'loading',
        caseDetailsFetchFailed: selectedCase !== null && detailsStatus === 'error',
    };
};
